import { FabMenuOptionId, TabType } from '@hunting-nature/shared-core/enums/tab-bar.enum';
import type { FabMenuOption, MenuConfig } from '@hunting-nature/shared-core/interfaces/tab-bar.interface';

/**
 * FAB menu options for the Cameras tab
 * Provides quick actions for adding new cameras
 */
export const CAMERAS_TAB_MENU_OPTIONS = [
  {
    id: FabMenuOptionId.Secacam,
    iconSrc: 'assets/tab-bar/overlay/product.svg',
    label: 'tabs.menu.secacam',
  },
  ...(FEATURE_BLE
    ? [
        {
          id: FabMenuOptionId.BleDevice,
          iconSrc: 'assets/tab-bar/overlay/hunting.svg',
          label: 'tabs.menu.addDevice',
        },
      ]
    : []),
] as const satisfies ReadonlyArray<FabMenuOption>;

/**
 * FAB menu options for the Gallery tab
 * Provides quick actions for managing galleries
 */
export const GALLERY_TAB_MENU_OPTIONS = [
  {
    id: FabMenuOptionId.CreateAlbum,
    iconSrc: 'assets/tab-bar/overlay/album.svg',
    label: 'tabs.menu.createNewAlbum',
  },
] as const satisfies ReadonlyArray<FabMenuOption>;

/**
 * Default empty menu options when no tab is active
 */
export const DEFAULT_TAB_MENU_OPTIONS: ReadonlyArray<FabMenuOption> = [];

/**
 * Map of tab types to their respective FAB menu configurations
 * Each tab can have its own set of menu options
 */
export const TAB_MENU_CONFIG_MAP: Record<TabType, MenuConfig> = {
  [TabType.Cameras]: { options: CAMERAS_TAB_MENU_OPTIONS },
  [TabType.Gallery]: { options: GALLERY_TAB_MENU_OPTIONS },
  /* Ballistics & Map pages to be restored in future release 
  [TabType.Ballistics]: { options: DEFAULT_TAB_MENU_OPTIONS },
  [TabType.Map]: { options: DEFAULT_TAB_MENU_OPTIONS }, */
};

/**
 * Retrieves the menu configuration for the given active tab
 *
 * @param activeTab - The currently active tab type
 * @returns The menu configuration including options and grid layout
 *
 * @example
 * const config = getMenuConfigForTab(TabType.Cameras);
 * // Returns { options: CAMERAS_TAB_MENU_OPTIONS, gridTemplate: TabBarMenuLayout.TwoColumns }
 */
export const getMenuConfigForTab = (activeTab: TabType | undefined): MenuConfig => {
  if (!activeTab) {
    return { options: DEFAULT_TAB_MENU_OPTIONS };
  }

  return TAB_MENU_CONFIG_MAP[activeTab] ?? { options: DEFAULT_TAB_MENU_OPTIONS };
};
